import { useThemeStore } from '@/store/theme'
import mapboxgl from 'mapbox-gl'
import { RefObject, useEffect, useRef, useState } from 'react'

interface UseMapInstanceOptions {
  accessToken: string
  containerRef: RefObject<HTMLDivElement | null>
  interactive: boolean
}

interface UseMapInstanceReturn {
  mapRef: React.RefObject<mapboxgl.Map | null>
  isReady: boolean
}

const DEFAULT_CENTER: [number, number] = [-98.5795, 39.8283] // Center of USA
const DEFAULT_ZOOM = 3 // Zoom level to show whole USA

const MAPBOX_STYLE_LIGHT = 'mapbox://styles/mapbox/light-v11'
const MAPBOX_STYLE_DARK = 'mapbox://styles/mapbox/dark-v11'

export const useMapInstance = ({
  accessToken,
  containerRef,
  interactive,
}: UseMapInstanceOptions): UseMapInstanceReturn => {
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const [isReady, setIsReady] = useState(false)
  const themeMode = useThemeStore((state) => state.mode)
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches
  )

  useEffect(() => {
    if (themeMode !== 'auto') return
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [themeMode])

  const isDarkMode =
    themeMode === 'dark' || (themeMode === 'auto' && systemDark)
  const mapStyle = isDarkMode ? MAPBOX_STYLE_DARK : MAPBOX_STYLE_LIGHT

  // Initialize map (only once)
  const mapStyleRef = useRef(mapStyle)
  useEffect(() => {
    mapStyleRef.current = mapStyle
  }, [mapStyle])

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    mapboxgl.accessToken = accessToken

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: mapStyleRef.current,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      attributionControl: false,
      interactive,
      projection: 'mercator',
    })

    map.on('load', () => {
      setIsReady(true)
    })

    mapRef.current = map

    return () => {
      mapRef.current?.remove()
      mapRef.current = null
      setIsReady(false)
    }
  }, [accessToken, containerRef, interactive])

  // Update map style when theme changes
  useEffect(() => {
    if (!mapRef.current || !isReady) return
    mapRef.current.setStyle(mapStyle)
  }, [mapStyle, isReady])

  return { mapRef, isReady }
}
